import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useDocumentTitle } from '../app/context.js';
import { LegalLinks } from './SignInPage.js';

/** Öffentlich (ohne Anmeldung): Kontaktformular – zweiter Kontaktweg neben der E-Mail im Impressum. */
export function ContactPage() {
  useDocumentTitle('Kontakt');
  const shownAt = useRef(Date.now());
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // Falle für Bots
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (state === 'sending') return;
    setError(null);
    if (!name.trim() || !/^\S+@\S+\.\S+$/.test(email.trim()) || message.trim().length < 10) {
      setError('Bitte gib Name, eine gültige E-Mail-Adresse und eine Nachricht (mindestens 10 Zeichen) an.');
      return;
    }
    setState('sending');
    try {
      if (import.meta.env.VITE_MOCK === 'true') {
        await new Promise((r) => setTimeout(r, 400));
      } else {
        const res = await fetch('/api/kontakt', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ name, email, message, website, shownAt: shownAt.current }),
        });
        if (res.status === 429) throw new Error('Gerade gehen sehr viele Nachrichten ein. Bitte versuche es in ein paar Minuten erneut.');
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? 'Die Nachricht konnte nicht gesendet werden.');
      }
      setState('sent');
    } catch (err) {
      setError((err as Error).message || 'Die Nachricht konnte nicht gesendet werden.');
      setState('idle');
    }
  }

  return (
    <div className="legal">
      <header className="legal__head">
        <Link to="/" className="wordmark" aria-label="Betriebsam – Start">
          Betriebsam
        </Link>
        <Link to="/" className="link-btn">
          ← Zurück
        </Link>
      </header>

      <h1 className="page__title">
        <span className="mute">Schreib</span>
        <br />
        uns.
      </h1>

      {state === 'sent' ? (
        <div className="alert alert--ok" role="status">
          Danke für deine Nachricht! Wir melden uns per E-Mail bei dir.
        </div>
      ) : (
        <form className="form contact" onSubmit={submit} noValidate>
          <p className="note">
            Fragen zu Betriebsam oder zum Datenschutz? Alternativ per E-Mail an <a href="mailto:kontakt@betriebsam.net">kontakt@betriebsam.net</a>.
          </p>
          <div className="grid-2 contact__row">
            <div className="field">
              <label className="field__label" htmlFor="c-name">Name</label>
              <input id="c-name" className="input" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} required />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="c-email">E-Mail</label>
              <input
                id="c-email"
                className="input"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={254}
                required
              />
            </div>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="c-message">Nachricht</label>
            <textarea id="c-message" className="input textarea" rows={7} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={5000} required />
            <span className="field__hint">{message.length} / 5000</span>
          </div>
          {/* Für Menschen unsichtbar; Bots füllen es aus. */}
          <div className="honeypot" aria-hidden="true">
            <label htmlFor="c-website">Website</label>
            <input id="c-website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </div>
          {error && (
            <div className="alert alert--error" role="alert">
              {error}
            </div>
          )}
          <p className="note">
            Wir verwenden deine Angaben nur, um deine Anfrage zu beantworten – mehr dazu im <Link to="/datenschutz">Datenschutz</Link>.
          </p>
          <button type="submit" className="btn btn--primary" disabled={state === 'sending'}>
            {state === 'sending' ? 'Wird gesendet …' : 'Nachricht senden'}
          </button>
        </form>
      )}
      <LegalLinks />
    </div>
  );
}
